import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@marine/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@marine/ui/popover';
import { Badge } from '@marine/ui/badge';
import { formatCurrency } from '@marine/utils/formatters';
import { useBoats } from '@/modules/inventory/hooks/useBoats';
import type { Boat } from '@/modules/inventory/types/boat.types';
import { resolveBoatImageUrl } from '@/modules/inventory/utils/media';

interface InventoryBoatComboboxProps {
  value?: string | null; // ULID string
  onChange: (boatId: string | null) => void; // ULID string
  className?: string;
  placeholder?: string;
  label?: string;
}

export function InventoryBoatCombobox({
  value,
  onChange,
  className,
  placeholder = 'Search inventory...',
  label,
}: InventoryBoatComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 250);

    return () => clearTimeout(handle);
  }, [searchQuery]);

  const boatFilters = useMemo(() => {
    const filters: Record<string, any> = { per_page: 25 };
    if (debouncedQuery) {
      filters.search = debouncedQuery;
    }
    return filters;
  }, [debouncedQuery]);

  const { data: boatsResponse, isLoading, isFetching } = useBoats(boatFilters);
  const boats = boatsResponse?.items || [];

  const selectedBoat = boats?.find((boat: Boat) => boat.id === value);
  const isLoadingBoats = isLoading || isFetching;
  const resolveImageUrl = (boat?: Boat | null) =>
    resolveBoatImageUrl(boat, 'thumb', { allowOriginalFallback: false });
  const resolveBoatName = (boat?: Boat | null) =>
    boat?.display_name ||
    boat?.name ||
    [boat?.model_year, boat?.manufacturer, boat?.model].filter(Boolean).join(' ');

  return (
    <div className={className}>
      {label && (
        <label className="text-sm font-medium mb-2 block">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedBoat ? (
              <div className="flex items-center gap-2 truncate">
                <div className="h-8 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-muted/30">
                  {resolveImageUrl(selectedBoat) ? (
                    <img
                      src={resolveImageUrl(selectedBoat) || ''}
                      alt={selectedBoat.display_name || selectedBoat.name || 'Boat'}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xs text-muted-foreground">
                      No image
                    </div>
                  )}
                </div>
                <span className="truncate">{resolveBoatName(selectedBoat) || 'Boat'}</span>
                {selectedBoat.hin_number && (
                  <Badge variant="outline" className="ml-2 shrink-0 text-xs font-mono">
                    {selectedBoat.hin_number}
                  </Badge>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Search by make, model, year, stock#, or HIN..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {!isLoadingBoats && (
                <CommandEmpty>
                  <div className="text-center py-6">
                    <Search className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No boats found in inventory</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try different search terms or add boats to inventory
                    </p>
                  </div>
                </CommandEmpty>
              )}
              <CommandGroup>
                {isLoadingBoats ? (
                  <div className="py-6 text-center text-sm">Loading inventory...</div>
                ) : (
                  boats?.map((boat: Boat) => (
                    <CommandItem
                      key={boat.id}
                      value={boat.id}
                      onSelect={() => {
                        onChange(boat.id === value ? null : boat.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          value === boat.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="mr-3 h-10 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-muted/30">
                        {resolveImageUrl(boat) ? (
                          <img
                            src={resolveImageUrl(boat) || ''}
                            alt={boat.display_name || boat.name || 'Boat'}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-2xs text-muted-foreground">
                            No image
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{resolveBoatName(boat) || 'Boat'}</span>
                          {boat.hin_number && (
                            <Badge variant="outline" className="text-xs font-mono">
                              {boat.hin_number}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {boat.length_ft && `${boat.length_ft}' • `}
                          {boat.condition || 'Condition not specified'}
                          {boat.exterior_color ? ` • ${boat.exterior_color}` : ''}
                          {boat.our_price !== null && boat.our_price !== undefined
                            ? ` • ${formatCurrency(Number(boat.our_price), {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}`
                            : ''}
                        </div>
                      </div>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
